import styled from '@emotion/styled';
import { css } from '@emotion/react';

import theme from 'styles/theme';

export type SelectSize = 'sm' | 'md' | 'lg' | 'xl';
export type SelectShape = 'default' | 'pill';

type FieldProps = {
  $size: SelectSize;
  $shape?: SelectShape;
  $fullWidth?: boolean;
  $color?: string;
  $borderColor?: string;
  $backgroundColor?: string;
  $hasIcon?: boolean;
};

type LabelProps = {
  $color?: string;
};

const sizeStyles: Record<SelectSize, ReturnType<typeof css>> = {
  sm: css`
    height: 32px;
    padding: 0 28px 0 10px;
    font-size: 12px;
  `,
  md: css`
    height: 38px;
    padding: 0 40px 0 16px;
    font-size: 14px;
  `,
  lg: css`
    height: 44px;
    padding: 0 44px 0 18px;
    font-size: 16px;
  `,
  xl: css`
    height: 48px;
    width: 256px;
    padding: 12px 44px 12px 32px;
    font-size: 12px;
    border-radius: 6px;
  `,
};

const shapeStyles: Record<SelectShape, ReturnType<typeof css>> = {
  default: css`
    border-radius: 6px;
  `,
  pill: css`
    border-radius: 999px;
  `,
};

export const Wrapper = styled.div<{ $fullWidth?: boolean }>`
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
`;

export const Label = styled.label<LabelProps>`
  font-family: 'Mangueira', sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  color: ${({ $color }) => $color || theme.colors.primaryText};
`;

export const FieldWrap = styled.div`
  position: relative;
  display: inline-flex;
  width: 100%;
`;

export const IconSlot = styled.span`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  pointer-events: none;
  color: ${theme.colors.gray};

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const StyledSelect = styled('select', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<FieldProps>`
  appearance: none;
  width: 100%;
  font-family: 'Mangueira', sans-serif;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s ease, opacity 0.2s ease;

  color: ${({ $color }) => $color || theme.colors.primaryText};
  background-color: ${({ $backgroundColor }) => $backgroundColor || '#fff'};
  border: 1px solid
    ${({ $borderColor, $shape }) =>
      $borderColor ||
      ($shape === 'pill' ? theme.colors.black : theme.colors.lightGray)};
  padding-left: ${({ $hasIcon }) => ($hasIcon ? '34px' : undefined)};

  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M3 5.5L8 10.5L13 5.5' stroke='%23262626' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;

  &:has(option[value='']:checked),
  &:invalid {
    color: #8c8c8c;
  }

  ${({ $size }) => sizeStyles[$size]}
  ${({ $shape = 'default' }) => shapeStyles[$shape]}

  option {
    color: ${theme.colors.primaryText};
  }

  option[value=''] {
    color: ${theme.colors.mediumGray};
  }

  &:invalid,
  &[value=''] {
    color: ${theme.colors.mediumGray};
  }

  ${({ $shape }) =>
    $shape === 'pill' &&
    css`
      border-radius: 999px;
    `}

  &:hover:not(:disabled) {
    border-color: ${({ $borderColor, $shape }) =>
      $borderColor ||
      ($shape === 'pill' ? theme.colors.black : theme.colors.mediumGray)};
  }

  &:focus {
    border-color: ${({ $borderColor }) => $borderColor || theme.colors.blue};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;
